import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../Configuration/MsalConfiguration';
import logger from 'Services/MyTuiProfileCustomLogger';

// Flag to prevent concurrent token renewal
let isRenewing = false;
let renewalPromise = null;

/**
 * Check if a token is expired or expiring soon (within 300 seconds)
 * @param {object} token - Token object with exp claim
 * @returns {boolean} - True if token is expired or expiring soon
 */
export const isTokenExpired = (token) => {
  if (!token || !token.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationBuffer = 300; // 5 minutes
  return token.exp - currentTime < expirationBuffer;
};

/**
 * Renew token silently with concurrency protection
 * @returns {Promise<string>} - ID token string
 */
export const renewTokenSilently = async () => {
  // If already renewing, return the existing promise
  if (isRenewing && renewalPromise) {
    return renewalPromise;
  }

  // Set renewing flag and create new promise
  isRenewing = true;

  const performRenewal = async () => {
    try {
      const account = getMsalAccount();
      if (!account) {
        throw new Error('No account found');
      }

      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      return response.idToken;
    } finally {
      // Reset flags after completion
      isRenewing = false;
      renewalPromise = null;
    }
  };

  renewalPromise = performRenewal();
  return renewalPromise;
};

/**
 * Get the current authenticated account
 * @returns {object|null} - MSAL account object or null
 */
export const getMsalAccount = () => {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

/**
 * Get MSAL ID token with silent acquisition and fallback logic
 * @returns {Promise<string>} - ID token string
 */
export const getMsalIdToken = async () => {
  try {
    const account = getMsalAccount();
    if (!account) {
      throw new Error('No account found. User must log in.');
    }

    // Try silent token acquisition
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      return response.idToken;
    } catch (silentError) {
      // Check if interaction is required or if iframe timed out
      const isInteractionRequired = silentError instanceof InteractionRequiredAuthError;
      const isIframeTimeout =
        silentError instanceof BrowserAuthError &&
        (silentError.errorCode === 'monitor_window_timeout' || silentError.errorCode === 'token_renewal_error');

      if (isInteractionRequired || isIframeTimeout) {
        logger.warn('Silent acquisition failed, attempting popup:', silentError.errorCode || silentError.message);
        // Try popup acquisition
        try {
          const response = await msalInstance.acquireTokenPopup(loginRequest);
          return response.idToken;
        } catch (popupError) {
          // Fallback to redirect
          logger.warn('Popup acquisition failed, redirecting:', popupError.message);
          await msalInstance.loginRedirect(loginRequest);
          throw new Error('Redirecting to login...');
        }
      }

      // For other errors, try with forceRefresh
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
        forceRefresh: true,
      });
      return response.idToken;
    }
  } catch (error) {
    logger.error('Error acquiring token:', error);
    throw error;
  }
};

/**
 * Get MSAL access token
 * @param {Array<string>} scopes - Optional scopes for the access token
 * @returns {Promise<string>} - Access token string
 */
export const getMsalToken = async (scopes = []) => {
  try {
    const account = getMsalAccount();
    if (!account) {
      throw new Error('No account found. User must log in.');
    }

    const tokenRequest = {
      scopes: scopes.length > 0 ? scopes : loginRequest.scopes,
      account: account,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (silentError) {
      if (silentError instanceof InteractionRequiredAuthError) {
        try {
          const response = await msalInstance.acquireTokenPopup(tokenRequest);
          return response.accessToken;
        } catch (popupError) {
          logger.warn('Popup acquisition failed for access token, redirecting:', popupError.message);
          await msalInstance.loginRedirect(tokenRequest);
          throw new Error('Redirecting to login...');
        }
      }
      throw silentError;
    }
  } catch (error) {
    logger.error('Error acquiring access token:', error);
    throw error;
  }
};

/**
 * Handle token expiration and renewal
 * @returns {Promise<string>} - New ID token string
 */
export const handleTokenExpiration = async () => {
  try {
    return await renewTokenSilently();
  } catch (error) {
    logger.error('Error handling token expiration:', error);
    throw error;
  }
};
